// /components/auth/AuthModal.tsx
'use client';

import { signIn } from 'next-auth/react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

type View = 'login' | 'register' | 'verify' | 'forgot-password';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void; // обычно: router.replace(pathname) в родителе
  redirectPath?: string;
  initialView?: 'login' | 'register';
}

/** ✅ Lazy-load форм (chunk splitting) */
const LoginForm = dynamic(() => import('./LoginForm'), {
  ssr: false,
  loading: () => <div className="text-slate-400 text-sm">Загрузка формы…</div>,
});

const ForgotPasswordForm = dynamic(() => import('./ForgotPasswordForm'), {
  ssr: false,
  loading: () => <div className="text-slate-400 text-sm">Загрузка формы…</div>,
});

const VerificationForm = dynamic(() => import('./VerificationForm'), {
  ssr: false,
  loading: () => <div className="text-slate-400 text-sm">Загрузка формы…</div>,
});

// RegisterForm у тебя named export: export const RegisterForm = ...
const RegisterForm = dynamic(() => import('./RegisterForm').then((m) => m.RegisterForm), {
  ssr: false,
  loading: () => <div className="text-slate-400 text-sm">Загрузка формы…</div>,
});

export default function AuthModal({
  open,
  onClose,
  onSuccess,
  redirectPath,
  initialView = 'login',
}: Props) {
  const router = useRouter();

  const [view, setView] = useState<View>(initialView);
  const [emailToVerify, setEmailToVerify] = useState<string | null>(null);
  const [passwordToSignIn, setPasswordToSignIn] = useState<string | null>(null);

  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevOpenRef = useRef(open);

  const titles = useMemo<Record<View, string>>(
    () => ({
      login: 'Вход в аккаунт',
      register: 'Создание аккаунта',
      verify: 'Подтвердите E-mail',
      'forgot-password': 'Восстановление пароля',
    }),
    [],
  );

  const resetState = useCallback(() => {
    setView('login');
    setEmailToVerify(null);
    setPasswordToSignIn(null);
  }, []);

  // Сбрасываем состояние только когда модалка открылась (false -> true)
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (!wasOpen && open) {
      setView(initialView);
      setEmailToVerify(null);
      setPasswordToSignIn(null);
    }
  }, [open, initialView]);

  // Чистим таймер при размонтировании
  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  /** ✅ Единая логика после успешного входа */
  const handleSuccess = useCallback(() => {
    onSuccess?.(); // например, обновить RSC через router.replace(...) в родителе
    onClose();

    if (redirectPath) {
      router.replace(redirectPath);
    } else {
      router.refresh();
    }
  }, [onClose, onSuccess, redirectPath, router]);

  /** ✅ Закрытие с мягким сбросом состояния (чтобы не мигало при анимации) */
  const handleClose = useCallback(() => {
    onClose();

    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(resetState, 250);
  }, [onClose, resetState]);

  const handleRegistrationSuccess = useCallback((data: { email: string; password?: string }) => {
    setEmailToVerify(data.email);
    if (data.password) setPasswordToSignIn(data.password);
    setView('verify');
  }, []);

  /** ✅ Автологин после подтверждения email */
  const handleAutoLogin = useCallback(async () => {
    if (!emailToVerify || !passwordToSignIn) {
      toast.error('Произошла ошибка. Пожалуйста, войдите вручную.');
      setView('login');
      return;
    }

    const toastId = toast.loading('Входим в аккаунт...');
    try {
      const result = await signIn('credentials', {
        email: emailToVerify,
        password: passwordToSignIn,
        redirect: false,
      });

      if (result?.ok && !result?.error) {
        toast.success('Вы вошли в аккаунт', { id: toastId });
        handleSuccess();
      } else {
        toast.error('Не удалось автоматически войти. Попробуйте вручную.', { id: toastId });
        setView('login');
      }
    } catch {
      toast.error('Ошибка входа. Попробуйте ещё раз.', { id: toastId });
      setView('login');
    }
  }, [emailToVerify, passwordToSignIn, handleSuccess]);

  const handlePasswordLinkSent = useCallback(() => {
    // можно закрывать сразу, без refresh
    onClose();
  }, [onClose]);

  const safeCallbackUrl = (redirectPath?: string) => {
    const origin = window.location.origin;
    const path = redirectPath?.startsWith('/') ? redirectPath : '/';
    return `${origin}${path}`;
  };

  const buildCallbackUrl = (redirectPath?: string) => {
    const origin = window.location.origin;

    // Разрешаем только same-origin редиректы
    if (redirectPath) {
      // относительный путь
      if (redirectPath.startsWith('/')) return new URL(redirectPath, origin).toString();

      // абсолютный same-origin (если вдруг прилетел)
      try {
        const u = new URL(redirectPath);
        if (u.origin === origin) return u.toString();
      } catch {}
    }

    return new URL('/', origin).toString();
  };

  const handleVk = useCallback(() => {
    window.location.href = "/api/vk/start";
  }, []);

  const handleGoogle = useCallback(() => {
    signIn('google', { callbackUrl: buildCallbackUrl(redirectPath) });
  }, [redirectPath]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-[450px] bg-slate-900 border-slate-700 p-8 text-white rounded-xl shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold text-sky-400">
            {titles[view]}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-6">
          {view === 'login' && (
            <LoginForm
              onForgotPassword={() => setView('forgot-password')}
              onSuccess={handleSuccess}
            />
          )}

          {view === 'register' && (
            <RegisterForm onRegisterSuccess={handleRegistrationSuccess} onSuccess={handleSuccess} />
          )}

          {view === 'verify' && emailToVerify && (
            <VerificationForm email={emailToVerify} onVerified={handleAutoLogin} />
          )}

          {view === 'forgot-password' && <ForgotPasswordForm onLinkSent={handlePasswordLinkSent} />}
        </div>

        {(view === 'login' || view === 'register') && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-slate-900 px-2 text-slate-400">Или продолжите с</span>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={handleVk}
                className="gap-2 h-12 flex-1 cursor-pointer bg-slate-800 border-slate-700 hover:bg-slate-700 text-white"
              >
                <Image src="/vk.svg" alt="vk" width={24} height={24} />
                VK
              </Button>

              <Button
                variant="outline"
                onClick={handleGoogle}
                className="gap-2 h-12 flex-1 cursor-pointer bg-slate-800 border-slate-700 hover:bg-slate-700 text-white"
              >
                <Image src="/google.svg" alt="Google" width={22} height={22} />
                Google
              </Button>
            </div>
          </>
        )}

        <div className="mt-6 text-center text-sm text-slate-400">
          {view === 'login' && (
            <p>
              Нет аккаунта?{' '}
              <button
                type="button"
                onClick={() => setView('register')}
                className="font-medium text-sky-400 hover:underline cursor-pointer"
              >
                Регистрация
              </button>
            </p>
          )}

          {view === 'register' && (
            <p>
              Уже есть аккаунт?{' '}
              <button
                type="button"
                onClick={() => setView('login')}
                className="font-medium text-sky-400 hover:underline cursor-pointer"
              >
                Войти
              </button>
            </p>
          )}

          {view === 'forgot-password' && (
            <p>
              Вспомнили пароль?{' '}
              <button
                type="button"
                onClick={() => setView('login')}
                className="font-medium text-sky-400 hover:underline cursor-pointer"
              >
                Войти
              </button>
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
